import React, { useContext, useState, useEffect } from "react";
import api from '../services/api';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";
import { FaUser, FaLock, FaRedo } from "react-icons/fa";
import { 
  Box, 
  Typography,
  Button,
  CircularProgress,
  LinearProgress
} from '@mui/material';

const Login = () => {
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaText, setCaptchaText] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // Generate random numeric CAPTCHA
  const generateCaptcha = () => {
    const numbers = "0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    setCaptchaText(result);
    setUserCaptcha("");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Simulate progress for loading
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress === 100) {
            return 0;
          }
          const diff = Math.random() * 10;
          return Math.min(oldProgress + diff, 90); // Cap at 90% until request completes
        });
      }, 500);
    } else {
      setProgress(0);
    }

    return () => {
      clearInterval(timer);
    };
  }, [loading]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setCaptchaError("");

    // Case-insensitive CAPTCHA validation
    if (userCaptcha.trim().toLowerCase() !== captchaText.toLowerCase()) {
      setCaptchaError("CAPTCHA verification failed. Please try again.");
      generateCaptcha();
      return;
    }

    setLoading(true);
    setProgress(10); // Initial progress

    try {
      const response = await api.post(
        "/Auth/login",
        { username, password },
        { withCredentials: true }
      );

      setProgress(100); // Complete progress before finishing
      
      const token = response.data.token;
      if (!token) {
        setError("Authentication failed: No token received");
        setLoading(false);
        return;
      }
      
      localStorage.setItem("token", token);
      login({ token });
      navigate("/");
    } catch (err) {
      let errorMessage = "Login failed";
      
      if (!err.response) {
        errorMessage = "Network error: Please check your internet connection";
      } else if (err.response.status === 401) {
        errorMessage = "Invalid username or password";
      } else if (err.response.status === 500) {
        errorMessage = "Server error: Please try again later";
      } else if (err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
      setLoading(false);
      generateCaptcha();
    }
  };

  // CAPTCHA display with random styling
  const renderCaptcha = () => {
    return captchaText.split('').map((char, index) => {
      const rotation = Math.floor(Math.random() * 30) - 15;
      const color = "#0b51ad";
      const fontSize = 24;
      
      return (
        <span 
          key={index}
          style={{
            transform: `rotate(${rotation}deg)`,
            color: color,
            display: 'inline-block',
            fontSize: `${fontSize}px`,
            fontWeight: 'bold',
            margin: '0 2px',
            userSelect: 'none'
          }}
        >
          {char}
        </span>
      );
    });
  };

  return (
    <div 
      className="flex justify-center items-start min-h-screen pt-[30px]"
      style={{ 
        paddingTop: "25px",
        backgroundImage: `
          url('/login-bg.png'),
          linear-gradient(to right, rgb(119, 163, 163), rgb(100, 100, 103))
        `,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/30 backdrop-blur-md p-8 rounded-xl shadow-2xl w-full max-w-lg text-center"
      >        
        <form onSubmit={handleLogin}>
          <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              LOGIN
            </Typography>
            
            {/* Loading progress bar */}
            {loading && (
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  mb: 2,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white'
                  }
                }} 
              />
            )}
            
            <div className="relative mb-4 text-left">
              <FaUser className="absolute left-3 top-3 text-gray-300" />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUserName(e.target.value)}
                className="border border-white/50 p-2 pl-10 rounded-lg w-full focus:ring-2 focus:ring-white outline-none bg-transparent placeholder-white/70"
                required
                disabled={loading}
              />
            </div>
    
            <div className="relative mb-4 text-left">
              <FaLock className="absolute left-3.5 top-3 text-gray-300" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border border-white/50 p-2 pl-12 rounded-lg w-full focus:ring-2 focus:ring-white outline-none bg-transparent placeholder-white/70"
                required
                disabled={loading}
              />
            </div>

            {/* CAPTCHA Section */}
            <div className="mb-0 bg-white/20 p-0 rounded-lg">
              <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <div className="bg-white-300 p-0 rounded-md">
                  {renderCaptcha()}
                </div>
              </div>
                <Button className="es-button"
                  onClick={generateCaptcha}
                  startIcon={<FaRedo />}
                  size="small"
                  width="150px"
                >
                  Refresh
                </Button>

              </div>
              
              <input
                type="text"
                placeholder="Type the code shown"
                value={userCaptcha}
                onChange={(e) => setUserCaptcha(e.target.value)}
                className="border border-white/50 p-2 rounded-lg w-full focus:ring-2 focus:ring-white outline-none bg-transparent placeholder-white/70"
                required
                disabled={loading}
              />
              
              {captchaError && (
                <p className="text-red-200 text-sm mt-1">{captchaError}</p>
              )}
            </div>
    
            {error && (
              <p className="text-red-200 mb-0">{error}</p>
            )}
    
    <Button 
  type="submit"
  variant="contained"              
  sx={{
    mt: 1,
    py: 0.7,
    minWidth: 150, // Added minWidth here
    fontWeight: 'bold',
    backgroundColor: 'rgb(39, 94, 246)',
    '&:hover': {
      backgroundColor: 'rgba(29, 120, 232, 0.93)'
    },
    '&:disabled': {
      backgroundColor: 'rgba(246, 244, 244, 0.93)',
      color: 'rgb(36, 36, 37)'
    }
  }}              
  disabled={loading}
  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
>
  {loading ? "AUTHENTICATING..." : "Login"}
</Button>
          </Box>      
        </form>
      </motion.div>
    </div>
  );
};

export default Login;